<template>
  <div v-if="loading"
       class="circular-loader">
    <base-circular-loader color="primary"
                          size="medium" />
  </div>

  <div v-else
       class="youtube-tab"
       :class="tabClasses">
    <base-switch class="modes-switch"
                 v-model="selectedMode"
                 :options="modes"
                 @change="modeChange"/>

    <div v-if="displayedDataExists"
         class="tab-content">
      <search-list v-if="searchesModeActive"
                   :searches="displayedData" />

      <youtube-views-list v-if="seenModeActive"
                          :views="displayedData" />
    </div>

    <div v-else
         class="empty-tab-placeholder">
      <searches-empty-screen v-if="searchesModeActive"
                             :loading="loading"
                             @retry="loadData" />

      <youtube-empty-screen v-if="seenModeActive"
                            :loading="loading"
                            @retry="loadData" />
    </div>
  </div>
</template>

<script>
  import YoutubeViewsList from '@/components/Child/ChildTabs/TabYouTube/YouTubeViewsList'
  import SearchList from '@/components/Child/ChildTabs/TabSearchList/SearchList'
  import SearchesEmptyScreen from '@/components/Child/ChildTabs/TabSearchList/SearchesEmptyScreen'
  import YoutubeEmptyScreen from '@/components/Child/ChildTabs/TabYouTube/YouTubeEmptyScreen'

  import youtubeModes from '@/constants/youtubeModes'

  import { addVideoTitles, updateYoutubeViewsData } from '@/services/youtube'

  export default {
    components: {
      YoutubeViewsList,
      SearchList,
      SearchesEmptyScreen,
      YoutubeEmptyScreen
    },

    props: {
      childId: {
        type: String,
        required: true
      }
    },

    data() {
      return {
        modes: youtubeModes,
        selectedMode: youtubeModes[0],
        displayedData: [],
        loading: false
      }
    },

    created() {
      this.loadData()
    },

    methods: {
      loadData() {
        this.loading = true

        return Promise.all([
          this.loadViews(),
          this.loadSearches()
        ]).then(() => {
          return addVideoTitles(this.youtubeViews)
        }).then(() => {
          updateYoutubeViewsData(this.youtubeViews)
          this.modeChange()
          this.loading = false
        })
      },

      loadSearches() {
        return this.setYoutubeSearches({
          user_id: this.childId,
          event_names: ['search']
        })
      },

      async loadViews() {
        return this.setYoutubeViews({
          user_id: this.childId,
          min_date: this.minDate,
          max_date: this.maxDate
        })
      },

      modeChange() {
        if (this.selectedMode.key === 'SEEN') {
          this.displayedData = this.youtubeViews
        }

        if (this.selectedMode.key === 'SEARCH') {
          this.displayedData = this.youtubeSearches
        }
      },

      ...mapActions('children', {
        setYoutubeSearches: 'setYoutubeSearches',
        setYoutubeViews: 'setYoutubeViews'
      })
    },

    computed: {
      child() {
        return this.getChildById(this.childId)
      },

      primaryDevice() {
        return this.getPrimaryDevice(this.childId)
      },

      minDate() {
        let minDate = new Date()
        minDate.setDate(minDate.getDate() - 30)

        return minDate
      },

      maxDate() {
        return new Date()
      },

      ...mapGetters('auth', {
        currentUser: 'currentUser'
      }),

      youtubeSearches() {
        return this.getYoutubeSearches(this.childId)
      },

      youtubeViews() {
        return this.getYoutubeViews(this.childId)
      },

      ...mapGetters('children', {
        getYoutubeSearches: 'getYoutubeSearches',
        getYoutubeViews: 'getYoutubeViews'
      }),

      ...mapGetters('children', {
        getChildById: 'getById',
        getPrimaryDevice: 'getPrimaryDevice'
      }),

      ...mapGetters('app', {
        visibilityState: 'getVisibilityState',
        isUserInactive: 'isUserInactive'
      }),

      tabClasses() {
        return {
          'youtube-tab-mobile': this.$mq === 'mobile'
        }
      },

      searchesModeActive() {
        return this.selectedMode.key === 'SEARCH'
      },

      seenModeActive() {
        return this.selectedMode.key === 'SEEN'
      },

      displayedDataExists() {
        return this.displayedData.length
      }
    },

    watch: {
      youtubeSearches() {
        if (this.selectedMode.key === 'SEEN') {
          this.displayedData = this.youtubeViews
        }

        if (this.selectedMode.key === 'SEARCH') {
          this.displayedData = this.youtubeSearches
        }
      },

      youtubeViews() {
        if (this.selectedMode.key === 'SEEN') {
          this.displayedData = this.youtubeViews
        }

        if (this.selectedMode.key === 'SEARCH') {
          this.displayedData = this.youtubeSearches
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .circular-loader {
    width: 100%;
    height: 100%;

    display: flex;
    justify-content: center;
    align-items: center;
  }

  .youtube-tab {
    width: 100%;
    height: 100%;
    padding-top: 24px;

    .modes-switch {
      width: 515px;
      flex-shrink: 0;
      margin: 0 auto 25px auto;
    }

    .tab-content {
      width: 100%;
      height: 100%;
    }

    .empty-tab-placeholder {
      width: 100%;
      height: 100%;
    }
  }

  .youtube-tab-mobile {
    padding-top: 12px;

    .modes-switch {
      width: 100%;
      padding: 0 10px;
      margin: 0;
    }
  }
</style>